import React, { useState, useEffect } from 'react';
import { connect } from "react-redux";
import { withRouter } from 'react-router-dom';
import './OrderManager.scss';
import { saveOrderData, loadOrderData, generateOrderId } from '../../utils/functions';
import {
    setGeoJson,
    setOrderId,
    setTitle,
    setLocation,
    setMessage,
    setElevation,
    setSmallText,
    setStyle,
    setMarkersWithText,
    setMarkersJson,
    setShowElevation,
    setShowStartStop,
    setCanvasSize,
    setZoom,
    setGpxArray
} from "../../actions";

const OrderManager = (props) => {
    const [loadOrderId, setLoadOrderId] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [showLoadSection, setShowLoadSection] = useState(false);

    const handleSaveOrder = async () => {
        if (!props.orderId || !props.geoJson) {
            setMessage('No order data to save');
            return;
        }

        setIsLoading(true);
        try {
            const orderData = {
                orderId: props.orderId,
                geoJson: props.geoJson,
                title: props.title,
                location: props.location,
                message: props.message,
                elevation: props.elevation,
                smallText: props.small,
                style: props.style,
                markersJson: props.markersJson,
                markersWithText: props.markersWithText || [],
                showElevation: props.showElevation,
                showStartStop: props.showStartStop,
                canvasSize: props.canvasSize,
                zoom: props.zoom,
                gpxArray: props.gpxArray || [],
                createdAt: new Date().toISOString(),
            };

            await saveOrderData(orderData);
            setMessage(`Order saved successfully! Order ID: ${props.orderId}`);
        } catch (error) {
            setMessage('Error saving order: ' + error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleLoadOrder = async () => {
        if (!loadOrderId.trim()) {
            setMessage('Please enter an Order ID');
            return;
        }

        setIsLoading(true);
        try {
            const orderData = await loadOrderData(loadOrderId.trim());

            // Load all the order data into Redux state
            if (orderData.geoJson) props.setGeoJson(orderData.geoJson);
            if (orderData.title) props.setTitle(orderData.title);
            if (orderData.location) props.setLocation(orderData.location);
            if (orderData.message) props.setMessage(orderData.message);
            if (orderData.elevation) props.setElevation(orderData.elevation);
            if (orderData.smallText) props.setSmallText(orderData.smallText);
            if (orderData.style) props.setStyle(orderData.style);
            if (orderData.markersJson) props.setMarkersJson(orderData.markersJson);
            if (orderData.markersWithText) props.setMarkersWithText(orderData.markersWithText);
            if (orderData.showElevation !== undefined) props.setShowElevation(orderData.showElevation);
            if (orderData.showStartStop !== undefined) props.setShowStartStop(orderData.showStartStop);
            if (orderData.canvasSize) props.setCanvasSize(orderData.canvasSize);
            if (orderData.zoom) props.setZoom(orderData.zoom);
            if (orderData.gpxArray) {
                props.setGpxArray(orderData.gpxArray);
                // Process gpxArray to geoJson for map display
                const geoJsonData = orderData.gpxArray.map(item => item.geoJson);
                props.setGeoJson(geoJsonData);
            }

            props.setOrderId(loadOrderId.trim());
            setMessage(`Order loaded successfully! Order ID: ${loadOrderId.trim()}`);
            setLoadOrderId('');
            setShowLoadSection(false);

            // Navigate to design page after loading order
            if (props.history) {
                props.history.push('/products/design-your-own-trail-map/design');
            }
        } catch (error) {
            setMessage('Error loading order: ' + error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleGenerateNewOrderId = () => {
        const newOrderId = generateOrderId();
        props.setOrderId(newOrderId);
        setMessage(`New Order ID generated: ${newOrderId}`);
    };

    return (
        <div className="order-manager">
            <div className="order-section">
                <h3>Current Order ID: {props.orderId || 'None'}</h3>
                
                <div className="order-actions">
                    <button 
                        onClick={handleGenerateNewOrderId}
                        className="btn-secondary"
                    >
                        Generate New Order ID
                    </button>
                    
                    <button 
                        onClick={handleSaveOrder}
                        disabled={isLoading || !props.geoJson}
                        className="btn-primary"
                    >
                        {isLoading ? 'Saving...' : 'Save Current Order'}
                    </button>
                </div>
            </div>

            <div className="order-section">
                <h3>
                    Load Existing Order
                    <button
                        onClick={() => setShowLoadSection(!showLoadSection)}
                        className="btn-secondary ml-2 text-sm"
                        style={{ fontSize: '12px', padding: '4px 8px' }}
                    >
                        {showLoadSection ? 'Hide' : 'Show'}
                    </button>
                </h3>
                {showLoadSection && (
                    <div className="load-order">
                        <input
                            type="text"
                            placeholder="Enter Order ID"
                            value={loadOrderId}
                            onChange={(e) => setLoadOrderId(e.target.value)}
                            className="order-input"
                        />
                        <button
                            onClick={handleLoadOrder}
                            disabled={isLoading || !loadOrderId.trim()}
                            className="btn-primary"
                        >
                            {isLoading ? 'Loading...' : 'Load Order'}
                        </button>
                    </div>
                )}
            </div>

            {message && (
                <div className={`order-message ${message.includes('Error') ? 'error' : 'success'}`}>
                    {message}
                </div>
            )}
        </div>
    );
};

const mapStateToProps = (state) => {
    return {
        orderId: state.orderId,
        geoJson: state.geoJson,
        title: state.title,
        location: state.location,
        message: state.message,
        elevation: state.elevation,
        small: state.small,
        style: state.style,
        markersJson: state.markersJson,
        markersWithText: state.markersWithText,
        showElevation: state.showElevation,
        showStartStop: state.showStartStop,
        canvasSize: state.canvasSize,
        zoom: state.zoom,
        gpxArray: state.gpxArray,
    };
};

export default withRouter(connect(mapStateToProps, {
    setGeoJson,
    setOrderId,
    setTitle,
    setLocation,
    setMessage,
    setElevation,
    setSmallText,
    setStyle,
    setMarkersWithText,
    setMarkersJson,
    setShowElevation,
    setShowStartStop,
    setCanvasSize,
    setZoom,
    setGpxArray
})(OrderManager));
